/**
 * Orientación MPR oblicua: matriz 3×3 en espacio de índices de volumen.
 * Identidad = axial/coronal/sagital estándar.
 */

/** @typedef {[number, number, number]} Vec3 */
/** @typedef {number[]} Mat3 row-major 3×3 */

/**
 * @returns {Mat3}
 */
export function identityMatrix() {
  return [1, 0, 0, 0, 1, 0, 0, 0, 1]
}

/**
 * @param {Mat3|null|undefined} m
 * @param {number} [eps]
 */
export function isIdentityMatrix(m, eps = 1e-5) {
  if (!m || m.length < 9) return true
  const I = identityMatrix()
  for (let i = 0; i < 9; i++) {
    if (Math.abs(Number(m[i]) - I[i]) > eps) return false
  }
  return true
}

/**
 * @param {Mat3} m
 * @returns {Mat3}
 */
export function cloneMatrix(m) {
  if (!m || m.length < 9) return identityMatrix()
  return [
    Number(m[0]) || 0,
    Number(m[1]) || 0,
    Number(m[2]) || 0,
    Number(m[3]) || 0,
    Number(m[4]) || 0,
    Number(m[5]) || 0,
    Number(m[6]) || 0,
    Number(m[7]) || 0,
    Number(m[8]) || 0,
  ]
}

/**
 * @param {Mat3} a
 * @param {Mat3} b
 * @returns {Mat3}
 */
export function mulMat3(a, b) {
  const o = new Array(9)
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      o[r * 3 + c] =
        a[r * 3 + 0] * b[0 * 3 + c] +
        a[r * 3 + 1] * b[1 * 3 + c] +
        a[r * 3 + 2] * b[2 * 3 + c]
    }
  }
  return o
}

/**
 * @param {Mat3} m
 * @param {Vec3} v
 * @returns {Vec3}
 */
export function mulMat3Vec(m, v) {
  return [
    m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
    m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
    m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
  ]
}

/**
 * @param {Vec3} v
 * @returns {Vec3}
 */
export function normalize(v) {
  const x = Number(v[0]) || 0
  const y = Number(v[1]) || 0
  const z = Number(v[2]) || 0
  const len = Math.sqrt(x * x + y * y + z * z)
  if (!(len > 0)) return [0, 0, 0]
  return [x / len, y / len, z / len]
}

/**
 * @param {Vec3} a
 * @param {Vec3} b
 * @returns {Vec3}
 */
export function cross(a, b) {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ]
}

/**
 * Rotación de Rodrigues alrededor de eje unitario.
 * @param {Vec3} axis
 * @param {number} angleRad
 * @returns {Mat3}
 */
export function rotateAroundAxis(axis, angleRad) {
  const n = normalize(axis)
  const x = n[0]
  const y = n[1]
  const z = n[2]
  const c = Math.cos(angleRad)
  const s = Math.sin(angleRad)
  const t = 1 - c
  return [
    t * x * x + c,
    t * x * y - s * z,
    t * x * z + s * y,
    t * x * y + s * z,
    t * y * y + c,
    t * y * z - s * x,
    t * x * z - s * y,
    t * y * z + s * x,
    t * z * z + c,
  ]
}

/**
 * Columnas de R = ejes transformados del volumen (ex, ey, ez).
 * @param {Mat3} m
 * @param {0|1|2} col
 * @returns {Vec3}
 */
export function matrixColumn(m, col) {
  return [m[col], m[3 + col], m[6 + col]]
}

/**
 * Frame del viewport lógico (sigue llamándose axial/coronal/sagittal).
 * @param {Mat3} matrix
 * @param {'axial'|'coronal'|'sagittal'} role
 * @returns {{ u: Vec3, v: Vec3, n: Vec3 }}
 */
export function planeFrame(matrix, role) {
  const m = matrix && matrix.length >= 9 ? matrix : identityMatrix()
  const ex = normalize(matrixColumn(m, 0))
  const ey = normalize(matrixColumn(m, 1))
  const ez = normalize(matrixColumn(m, 2))
  const r = String(role || 'axial').toLowerCase()
  if (r === 'coronal') {
    return { u: ex, v: ez, n: ey }
  }
  if (r === 'sagittal') {
    return { u: ey, v: ez, n: ex }
  }
  return { u: ex, v: ey, n: ez }
}

/**
 * Aplica rotación alrededor de la normal del plano activo (estilo OHIF).
 * @param {Mat3} matrix
 * @param {'axial'|'coronal'|'sagittal'} activeRole
 * @param {number} deltaRad
 * @returns {Mat3}
 */
export function applyObliqueRotate(matrix, activeRole, deltaRad) {
  const base = cloneMatrix(matrix)
  const angle = Number(deltaRad)
  if (!Number.isFinite(angle) || angle === 0) return base
  const { n } = planeFrame(base, activeRole)
  if (!n[0] && !n[1] && !n[2]) return base
  const dR = rotateAroundAxis(n, angle)
  return mulMat3(dR, base)
}

function dot3(a, b) {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
}

/**
 * Frame de display con up de mundo estable (líneas giran en pantalla al rotar).
 * Alinea u/v con el MPR clásico en identidad (sin flip sagital).
 * @param {Mat3} matrix
 * @param {'axial'|'coronal'|'sagittal'} role
 * @returns {{ u: Vec3, v: Vec3, n: Vec3 }}
 */
export function planeFrameForDisplay(matrix, role) {
  const raw = planeFrame(matrix, role)
  const n = normalize(raw.n)
  if (!n[0] && !n[1] && !n[2]) return raw
  let ref = [0, 0, 1]
  if (Math.abs(dot3(n, ref)) > 0.92) ref = [0, 1, 0]
  let u = normalize(cross(n, ref))
  if (!u[0] && !u[1] && !u[2]) {
    ref = [1, 0, 0]
    u = normalize(cross(n, ref))
  }
  // Emparejar sentido con el frame clásico
  if (dot3(u, raw.u) < 0) u = [-u[0], -u[1], -u[2]]
  // v = n × u (no u × n) para no invertir filas en sagital
  let v = normalize(cross(n, u))
  if (dot3(v, raw.v) < 0) v = [-v[0], -v[1], -v[2]]
  return { u, v, n }
}

/**
 * Tamaño de salida sugerido por rol (igual que MPR ortogonal actual).
 * @param {{ cols: number, rows: number, zDim: number }} size
 * @param {'axial'|'coronal'|'sagittal'} role
 */
export function planeOutputSize(size, role) {
  const cols = Math.max(1, Number(size && size.cols) || 1)
  const rows = Math.max(1, Number(size && size.rows) || 1)
  const zDim = Math.max(1, Number(size && size.zDim) || 1)
  const r = String(role || 'axial').toLowerCase()
  if (r === 'coronal') return { outCols: cols, outRows: zDim }
  if (r === 'sagittal') return { outCols: rows, outRows: zDim }
  return { outCols: cols, outRows: rows }
}

/**
 * Spacings de display (mm) a partir de ejes u/v en índice de vóxel.
 * @param {Vec3} u
 * @param {Vec3} v
 * @param {{ sx: number, sy: number, sz: number }} spacing
 */
export function planePixelSpacing(u, v, spacing) {
  const sx = Number(spacing && spacing.sx) || 1
  const sy = Number(spacing && spacing.sy) || 1
  const sz = Number(spacing && spacing.sz) || 1
  const colMm = Math.sqrt(
    (u[0] * sx) ** 2 + (u[1] * sy) ** 2 + (u[2] * sz) ** 2
  )
  const rowMm = Math.sqrt(
    (v[0] * sx) ** 2 + (v[1] * sy) ** 2 + (v[2] * sz) ** 2
  )
  return {
    columnPixelSpacing: colMm > 0 ? colMm : 1,
    rowPixelSpacing: rowMm > 0 ? rowMm : 1,
  }
}

/**
 * Pixel imagen (col,row) del centro en un plano oblicuo.
 * @param {{ outCols: number, outRows: number }} out
 */
export function obliqueCenterPixel(out) {
  const oc = Math.max(1, Number(out && out.outCols) || 1)
  const or_ = Math.max(1, Number(out && out.outRows) || 1)
  return {
    x: Math.round((oc - 1) / 2),
    y: Math.round((or_ - 1) / 2),
  }
}

/**
 * Mueve el centro a lo largo de la normal del plano (scroll oblicuo).
 * @param {{x:number,y:number,z:number}} center
 * @param {Vec3} n
 * @param {number} deltaSlices
 * @param {{ cols: number, rows: number, zDim: number }} size
 */
export function stepCenterAlongNormal(center, n, deltaSlices, size) {
  const d = Number(deltaSlices) || 0
  const next = {
    x: Number(center.x) + n[0] * d,
    y: Number(center.y) + n[1] * d,
    z: Number(center.z) + n[2] * d,
  }
  const maxX = Math.max(0, (size.cols || 1) - 1)
  const maxY = Math.max(0, (size.rows || 1) - 1)
  const maxZ = Math.max(0, (size.zDim || 1) - 1)
  return {
    x: Math.max(0, Math.min(maxX, Math.round(next.x))),
    y: Math.max(0, Math.min(maxY, Math.round(next.y))),
    z: Math.max(0, Math.min(maxZ, Math.round(next.z))),
  }
}

/**
 * Pixel → desplazamiento en plano → nuevo centro (aim oblicuo).
 * @param {{x:number,y:number}} pixel
 * @param {{x:number,y:number,z:number}} center
 * @param {{ u: Vec3, v: Vec3 }} frame
 * @param {{ outCols: number, outRows: number }} out
 * @param {{ cols: number, rows: number, zDim: number }} size
 */
export function obliquePixelToCenter(pixel, center, frame, out, size) {
  const cx = (out.outCols - 1) / 2
  const cy = (out.outRows - 1) / 2
  const dc = (Number(pixel && pixel.x) || 0) - cx
  const dr = (Number(pixel && pixel.y) || 0) - cy
  const u = frame.u
  const v = frame.v
  const next = {
    x: Number(center.x) + dc * u[0] + dr * v[0],
    y: Number(center.y) + dc * u[1] + dr * v[1],
    z: Number(center.z) + dc * u[2] + dr * v[2],
  }
  const maxX = Math.max(0, (size.cols || 1) - 1)
  const maxY = Math.max(0, (size.rows || 1) - 1)
  const maxZ = Math.max(0, (size.zDim || 1) - 1)
  return {
    x: Math.max(0, Math.min(maxX, Math.round(next.x))),
    y: Math.max(0, Math.min(maxY, Math.round(next.y))),
    z: Math.max(0, Math.min(maxZ, Math.round(next.z))),
  }
}
